import React, { useState, useRef } from "react";
import Card from "components/card";
import { MdEventNote, MdArrowUpward, MdArrowDownward } from "react-icons/md";
import { useUserCheckIns } from "../../hooks/useCheckIns";
import { UserCheckIn } from "../../types/checkin";
import CardHeader from "../common/CardHeader";
import DateFilterPicker from "../common/DateFilterPicker";
import DetailsButton from "../common/DetailsButton";
import EmptyState from "../common/EmptyState";
import ErrorMessage from "../common/ErrorMessage";
import LoadingSpinner from "../common/LoadingSpinner";
import Pagination from "../common/Pagination";
import CheckInDetailsModal from "./CheckInDetailsModal";
import { AdjustmentDecisionBadge, LifestyleZoneBadge } from "./checkInBadges";
import { formatDateLong } from "../../utils/dateUtils";
import {
  EMPTY_VALUE,
  formatOneDecimalWithUnit,
  formatSignedOneDecimalWithUnit,
  formatSignedPercent,
  signTone,
} from "../../utils/formatters";

interface UserCheckInsCardProps {
  userId: number | null;
}

// Coloured by numeric sign (negative red, positive green), not by goal.
const weightChange = (checkIn: UserCheckIn) => {
  const raw = checkIn.weight_change_pct;
  const formatted = formatSignedPercent(raw);
  if (formatted === null) {
    return <span className="text-gray-400">{EMPTY_VALUE}</span>;
  }

  const pct = parseFloat(raw as string);

  return (
    <span
      className={`flex items-center gap-1 ${signTone(
        pct,
        "text-gray-500 dark:text-gray-400"
      )}`}
    >
      {pct > 0 ? (
        <MdArrowUpward className="h-3.5 w-3.5" />
      ) : pct < 0 ? (
        <MdArrowDownward className="h-3.5 w-3.5" />
      ) : null}
      {formatted}
    </span>
  );
};

const kcalDelta = (checkIn: UserCheckIn) => {
  const delta = checkIn.applied_kcal_delta;
  if (delta === null || delta === undefined) {
    return <span className="text-gray-400">{EMPTY_VALUE}</span>;
  }
  if (delta === 0) {
    return <span className="text-gray-500 dark:text-gray-400">0 kcal</span>;
  }
  return (
    <span className="font-medium text-navy-700 dark:text-white">
      {formatSignedOneDecimalWithUnit(delta, "kcal")}
    </span>
  );
};

const averageWeight = (checkIn: UserCheckIn): string =>
  formatOneDecimalWithUnit(checkIn.average_weight_kg, "kg");

const UserCheckInsCard: React.FC<UserCheckInsCardProps> = ({ userId }) => {
  const {
    checkIns,
    loading,
    error,
    total,
    page,
    limit,
    filters,
    totalPages,
    updateFilters,
    changePage,
  } = useUserCheckIns(userId);

  // Paging scrolls here, not to the top of the page
  const sectionRef = useRef<HTMLDivElement>(null);
  const [selectedCheckIn, setSelectedCheckIn] = useState<UserCheckIn | null>(
    null
  );

  const hasFilters = !!filters.submitted_at;

  const handleDateChange = (value: string | undefined) => {
    updateFilters({ ...filters, submitted_at: value || undefined });
  };

  const renderBody = () => {
    if (loading) return <LoadingSpinner />;

    if (error) return <ErrorMessage message={error} />;

    if (checkIns.length === 0) {
      return (
        <EmptyState
          icon={MdEventNote}
          title={
            hasFilters
              ? "No check-ins match this date."
              : "No check-ins recorded for this user."
          }
        />
      );
    }

    return (
      <>
        {/* Desktop Table */}
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                {[
                  "Date",
                  "Avg Weight",
                  "Change",
                  "Decision",
                  "Zone",
                  "Kcal Δ",
                  "Details",
                ].map((heading) => (
                  <th
                    key={heading}
                    className="pb-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {checkIns.map((checkIn) => (
                <tr
                  key={checkIn.id}
                  onClick={() => setSelectedCheckIn(checkIn)}
                  className="cursor-pointer border-b border-gray-200 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                >
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-300">
                    {formatDateLong(checkIn.submitted_at)}
                  </td>
                  <td className="py-4 text-sm font-medium text-navy-700 dark:text-white">
                    {averageWeight(checkIn)}
                  </td>
                  <td className="py-4 text-sm">{weightChange(checkIn)}</td>
                  <td className="py-4 text-sm">
                    <AdjustmentDecisionBadge
                      decision={checkIn.adjustment_decision}
                    />
                  </td>
                  <td className="py-4 text-sm">
                    <LifestyleZoneBadge zone={checkIn.lifestyle_zone} />
                  </td>
                  <td className="py-4 text-sm">{kcalDelta(checkIn)}</td>
                  <td className="py-4 text-sm">
                    <DetailsButton
                      onClick={() => setSelectedCheckIn(checkIn)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="space-y-4 md:hidden">
          {checkIns.map((checkIn) => (
            <div
              key={checkIn.id}
              onClick={() => setSelectedCheckIn(checkIn)}
              className="cursor-pointer rounded-lg border border-gray-200 p-4 dark:border-gray-700"
            >
              <div className="mb-3 flex items-start justify-between">
                <div>
                  <p className="font-medium text-navy-700 dark:text-white">
                    {averageWeight(checkIn)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDateLong(checkIn.submitted_at)}
                  </p>
                </div>
                <div className="text-sm">{weightChange(checkIn)}</div>
              </div>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <AdjustmentDecisionBadge
                  decision={checkIn.adjustment_decision}
                />
                <LifestyleZoneBadge zone={checkIn.lifestyle_zone} />
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm">{kcalDelta(checkIn)}</div>
                <DetailsButton onClick={() => setSelectedCheckIn(checkIn)} />
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <Card extra="w-full p-3 md:p-6">
      <CardHeader
        title="Check-In History"
        count={total}
        singular="Check-In"
        plural="Check-Ins"
        scrollRef={sectionRef}
      />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <DateFilterPicker
          label="Submitted Date"
          value={filters.submitted_at}
          onChange={handleDateChange}
          placeholder="Any date"
        />
      </div>

      {renderBody()}

      <Pagination
        page={page}
        limit={limit}
        total={total}
        totalPages={totalPages}
        entityName="check-ins"
        onPageChange={changePage}
        loading={loading}
        scrollTargetRef={sectionRef}
      />

      <CheckInDetailsModal
        isOpen={!!selectedCheckIn}
        onClose={() => setSelectedCheckIn(null)}
        checkIn={selectedCheckIn}
      />
    </Card>
  );
};

export default UserCheckInsCard;
